import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Loader2, Camera } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAudit, useAuditItems, signedAuditPhotoUrl } from "@/features/audits/api";
import { useLookups } from "@/features/assets/api";
import { DocumentViewerDialog } from "@/components/documents/DocumentViewerDialog";
import { toast } from "sonner";
import { ConditionBadge, DiscrepancyBadge } from "@/features/audits/badges";
import { formatDate } from "@/features/assets/format";


export const Route = createFileRoute("/_authenticated/audits/$auditId/")({
  component: AuditOverview,
});

function AuditOverview() {
  const { auditId } = Route.useParams();
  const { data: audit } = useAudit(auditId);
  const { data: items = [], isLoading } = useAuditItems(auditId);
  const { data: lookups } = useLookups();
  const [photoViewer, setPhotoViewer] = useState<string | null>(null);

  if (!audit) return null;

  const nameMap = (rows: { id: string; name: string }[], ids: string[]) =>
    ids.map((id) => rows.find((r) => r.id === id)?.name ?? id).join(", ") || "All";

  return (
    <div className="space-y-4">
      <Card><CardContent className="grid grid-cols-1 gap-2 p-4 text-sm md:grid-cols-2">
        <div><span className="text-muted-foreground">Branches:</span> {nameMap(lookups?.branches ?? [], audit.scope.branch_ids ?? [])}</div>
        <div><span className="text-muted-foreground">Departments:</span> {nameMap(lookups?.departments ?? [], audit.scope.department_ids ?? [])}</div>
        <div><span className="text-muted-foreground">Categories:</span> {nameMap(lookups?.categories ?? [], audit.scope.category_ids ?? [])}</div>
        {audit.scope.sub_category_ids && audit.scope.sub_category_ids.length > 0 && (
          <div><span className="text-muted-foreground">Asset Names:</span> {nameMap(lookups?.categories ?? [], audit.scope.sub_category_ids)}</div>
        )}
        <div><span className="text-muted-foreground">Criticality:</span> {(audit.scope.criticality ?? []).join(", ") || "All"}</div>
        <div><span className="text-muted-foreground">Auditors:</span> {(audit.assigned_auditors ?? []).map((id) => (lookups?.users ?? []).find((u) => u.id === id)?.full_name ?? id).join(", ") || "—"}</div>
      </CardContent></Card>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">All Items ({items.length})</h2>
        <Button asChild size="sm"><Link to="/audits/$auditId/execute" params={{ auditId }}>Open Execution</Link></Button>
      </div>

      <Card><CardContent className="p-0">
        {isLoading ? <div className="grid place-items-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div> :
        items.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">No items.</div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Asset</TableHead><TableHead>Expected</TableHead>
              <TableHead>Found</TableHead><TableHead>Condition</TableHead><TableHead>Discrepancy</TableHead>
              <TableHead>Photo</TableHead><TableHead>Verified</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id}>
                  <TableCell className="text-sm">
                    {it.is_untagged_stub ? (
                      <div className="flex flex-col gap-1">
                        <div className="italic text-muted-foreground">Untagged: {it.untagged_description}</div>
                      </div>
                    ) : (
                      <>
                        <div className="font-mono text-xs">{it.asset?.asset_code}</div>
                        <div className="text-xs text-muted-foreground">{it.asset?.name}</div>
                      </>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{it.expected_location?.name ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{it.found_location?.name ?? "—"}</TableCell>
                  <TableCell><ConditionBadge condition={it.condition} /></TableCell>
                  <TableCell><DiscrepancyBadge type={it.discrepancy_type} /></TableCell>
                  <TableCell>
                    {it.photo_url ? (
                      <button 
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const url = await signedAuditPhotoUrl(it.photo_url!);
                            setPhotoViewer(url);
                          } catch (err) {
                            toast.error("Failed to load photo");
                          }
                        }} 
                        className="text-xs text-blue-600 hover:underline flex items-center gap-1 cursor-pointer"
                      >
                        <Camera className="h-3 w-3" /> View
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {it.verified_at ? (
                      <>
                        <div>{formatDate(it.verified_at)}</div>
                        <div>{it.verifier?.full_name ?? it.verifier?.email ?? ""}</div>
                      </>
                    ) : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
      <DocumentViewerDialog
        open={!!photoViewer}
        onOpenChange={(o) => { if (!o) setPhotoViewer(null); }}
        bucket="asset-docs"
        path={photoViewer}
        fileName="photo.jpg"
      />
    </div>
  );
}
